import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useForm } from "react-hook-form";
import toast from "react-hot-toast";
import { Fade } from "react-awesome-reveal";
import { yupResolver } from "@hookform/resolvers/yup";
import * as yup from "yup";
import { BsGenderAmbiguous } from "react-icons/bs";
import UserIcon from "../../../../assets/icons/profile.svg";
import UsersIcon from "../../../../assets/icons/users.svg";
import CalendarIcon from "../../../../assets/icons/calendar.svg";
import Input from "../../../form/input";
import Select from "../../../form/select";
import { FileUpload } from "../../../form/fileUpload";
import DatePickerInput from "../../../form/datePicker";
import Loading from "../../../common/loading";
import LoadingButton from "../../../common/loadingButton";
import useStore from "../../../../features/store";
import { genderOptions } from "../../../../data/SelectOptions";
import {
  formateDateToGregorian,
  formateDateToJalali,
  formateStringDateToGregorian,
  scrollToTop,
} from "../../../../utils/Functions";
import Textarea from "../../../form/textarea";

const schema = yup.object().shape({
  first_name: yup.string().required("نام الزامی است"),
  last_name: yup.string().required("نام‌خانوادگی الزامی است"),
  gender: yup.string().required("جنسیت الزامی است"),
  name: yup.string(),
  birth_day: yup.string(),
  terms: yup.bool().oneOf([true], ""),
});

const BasicInfo = ({
  tempProfile,
  isForAnother,
  setTestData,
  setStep,
}: {
  tempProfile: any;
  setTestData: any;
  isForAnother: any;
  setStep: any;
}) => {
  const isTempProfileExist = tempProfile !== "new-user";
  //@ts-ignore
  const { profile, isLoadingProfile, fetchProfile } = useStore();

  const [files, setFiles] = useState<any | null>();
  const [testDate, setTestDate] = useState<any>("");
  const [date, setDate] = useState<any>(
    isForAnother && isTempProfileExist
      ? formateDateToJalali(tempProfile?.birth_day)
      : !isForAnother && profile.birth_day
      ? formateDateToJalali(profile.birth_day)
      : ""
  );

  const {
    handleSubmit,
    formState: { errors, isValid, isDirty },
    control,
    setValue,
    ...form
  } = useForm({
    resolver: yupResolver(schema),
    mode: "onTouched",
  });

  useEffect(() => {
    if (!isForAnother && !profile) {
      fetchProfile();
    }

    if (profile && !isForAnother) {
      setValue("first_name", profile.user.first_name);
      setValue("last_name", profile.user.last_name);
      setValue("gender", profile.gender || "");
    }

    if (isForAnother && isTempProfileExist) {
      setValue("first_name", tempProfile?.first_name);
      setValue("last_name", tempProfile?.last_name);
      setValue("gender", tempProfile?.gender || "");
      setValue("name", tempProfile?.name || "");
      setValue("name", tempProfile?.name || "");
      setValue("birth_day", tempProfile?.birth_day || "");
    }
  }, [isForAnother]);

  const onSubmit = (data: any) => {
    if (!files || files.length === 0) {
      toast.error("حداقل یک تصویر بارگذاری کنید");
      return;
    }

    const formData = new FormData();

    formData.append("first_name", data.first_name);
    formData.append("last_name", data.last_name);
    formData.append("gender", data.gender);
    formData.append(
      "birth_day",
      typeof date === "string"
        ? formateStringDateToGregorian(date)
        : formateDateToGregorian(date)
    );
    formData.append(
      "test_date",
      typeof testDate === "string"
        ? formateStringDateToGregorian(testDate)
        : formateDateToGregorian(testDate)
    );
    formData.append("is_for_other", isForAnother);
    formData.append("gender", data.gender);
    formData.append("name", data.name);
    formData.append("causes_and_symptoms", data.causes_and_symptoms);

    files.forEach((file: File, index: number) => {
      formData.append(`files[${index}]`, file);
    });

    setTestData(formData);
    scrollToTop();
    setStep(3);
  };

  return (
    <div className="w-full flex items-start justify-start mb-10 mt-16">
      <Fade triggerOnce className="w-full">
        {!isForAnother && isLoadingProfile ? (
          <Loading type="basic-info" />
        ) : (
          <form onSubmit={handleSubmit(onSubmit)} className="flex-1 w-full">
            {!isForAnother && (
              <div className="border rounded-xl p-4 mb-3">
                <p className="font-rokh font-[900] pt-1 text-center">
                  می‌تونی اطلاعات شخصیت رو از بخش{" "}
                  <Link
                    to="/profile/update?redirect=test-registration"
                    className="font-rokh font-[900] pt-1 text-pr_primary underline"
                  >
                    پروفایل
                  </Link>{" "}
                  ویرایش کنی
                </p>
              </div>
            )}
            <div className="flex flex-col lg:flex-row justify-between items-start gap-0 lg:gap-8">
              <div className="flex-1 w-full">
                {isForAnother && (
                  <Input
                    form={form}
                    errors={errors}
                    title="* نام:"
                    icon={<img src={UserIcon} alt="نام" />}
                    placeholder=""
                    id="first_name"
                    type="text"
                    direction="rtl"
                    disabled={isTempProfileExist}
                  />
                )}
                {isForAnother && (
                  <Input
                    form={form}
                    errors={errors}
                    title="* نام‌خانوادگی:"
                    icon={<img src={UsersIcon} alt="نام‌خانوادگی" />}
                    placeholder=""
                    id="last_name"
                    type="text"
                    direction="rtl"
                    disabled={isTempProfileExist}
                  />
                )}
                <DatePickerInput
                  title="* تاریخ تولد:"
                  id="birth_day"
                  date={date}
                  setDate={setDate}
                  disabled={
                    !isForAnother || (isForAnother && isTempProfileExist)
                  }
                  icon={<img src={CalendarIcon} alt="سن" />}
                />
                <Select
                  form={form}
                  errors={errors}
                  title="* جنسیت:"
                  icon={<BsGenderAmbiguous size={23} />}
                  placeholder=""
                  id="gender"
                  options={genderOptions}
                  disabled={
                    !isForAnother || (isForAnother && isTempProfileExist)
                  }
                />
                <DatePickerInput
                  title="* تاریخ انجام آزمایش:"
                  id="test_date"
                  date={testDate}
                  setDate={setTestDate}
                  icon={<img src={CalendarIcon} alt="تاریخ انجام آزمایش" />}
                />
                {isForAnother && (
                  <Input
                    form={form}
                    errors={errors}
                    title="نسبت:"
                    icon={<img src={UsersIcon} alt="نسبت" />}
                    placeholder=""
                    id="name"
                    type="text"
                    direction="rtl"
                    disabled={isTempProfileExist}
                  />
                )}
                <Textarea
                  form={form}
                  errors={errors}
                  title="دلایل و علائم آزمایش:"
                  rows={4}
                  id="causes_and_symptoms"
                />

                <div className="flex lg:hidden my-5">
                  <FileUpload setFiles={setFiles} />
                </div>

                <div className="form-control mb-1">
                  <label
                    className="label cursor-pointer"
                    style={{ direction: "ltr" }}
                  >
                    <span className="label-text text-right w-full mr-2">
                      .تایید می‌کنم که{" "}
                      <Link
                        to="/rules-and-regulations"
                        target="_blank"
                        className="text-blue-500"
                      >
                        قوانین و مقررات
                      </Link>{" "}
                      رو خوندم و می‌پذیرم
                    </span>
                    <input
                      id="terms"
                      type="checkbox"
                      className="checkbox checkbox-sm border-gray-500"
                      {...form.register("terms")}
                    />
                  </label>
                </div>
              </div>

              <div className="hidden lg:flex flex-1">
                <FileUpload setFiles={setFiles} />
              </div>
            </div>

            <div className="flex justify-start lg:justify-center mt-5">
              <div className="w-full lg:w-fit min-w-[200px]">
                <LoadingButton
                  title="ادامه"
                  type="submit"
                  disabled={
                    !isValid ||
                    !isDirty ||
                    !files ||
                    files.length === 0 ||
                    !testDate
                  }
                />
              </div>
            </div>
          </form>
        )}
      </Fade>
    </div>
  );
};

export default BasicInfo;
